/**
 * 치과의사 개인 계정이 카페 공고 글을 붙였을 때의 답(2026-10-10 사용자 결정 "중간안", docs/ai-chat/prompt-engine-plan.md 7장).
 *
 * - 고칠 점은 답 본문(모델)에 최대 3줄 한 번만 — 카드 아래 "등록할 때 채울 칸"·"확인해 주세요" 목록과 [채용 공고 보기] 링크는 그리지 않는다.
 * - 버튼 [치과 계정으로 가입하고 이대로 올리기] → 병/의원 가입(`/signup?type=clinic`). 공고 초안(ai_recruitment_drafts)은 회원별이라
 *   새 치과 계정으로 넘어가지 않으므로, 누를 때 붙였던 공고 원문을 이 탭의 sessionStorage 에 담아 두고
 *   가입·로그인한 치과 계정의 첫 AI 화면 입력칸에 채운다(보내지는 않는다 — 회원이 그대로 보내면 같은 카드가 나온다).
 *
 * 화면 부품 없이 쓰는 규칙만 모았다(node --test 로 검사 — tests/aiDentistRecruitment.test.ts).
 */

/** 미리보기에서 보는 칸만 */
export interface DentistPreviewLike {
    poster: string;
}

/** 치과의사 개인 계정의 공고 미리보기 답인지 */
export const isDentistPreview = (preview: DentistPreviewLike | null | undefined): boolean => preview?.poster === 'DENTIST';

/** 카드 아래 "등록할 때 채울 칸"·"확인해 주세요" 목록을 그릴지 — 치과의사 개인은 답 본문의 고칠 점과 겹쳐 그리지 않는다 */
export const showPreviewFieldNotes = (preview: DentistPreviewLike): boolean => !isDentistPreview(preview);

/** 답 아래 링크([채용 공고 보기] 등)를 그릴지 — 치과의사 개인 공고 답은 빼고 가입 버튼만 둔다 */
export const showAnswerLinks = (preview: DentistPreviewLike | null | undefined): boolean => !isDentistPreview(preview);

/** 주 버튼 위 한 줄 안내(확정 문구) */
export const DENTIST_JOIN_HINT: string = '치과 계정으로 가입하면 이 공고를 그대로 올릴 수 있어요.';
/** 주 버튼(확정 문구) */
export const DENTIST_JOIN_LABEL: string = '치과 계정으로 가입하고 이대로 올리기';

/** 병/의원 가입 — 유형 선택을 건너뛰고 약관부터, 가입·로그인 뒤 AI 화면으로 */
export const CLINIC_SIGNUP_PATH: string = `/signup?type=clinic&returnUrl=${encodeURIComponent('/ai')}`;

/** 가입 화면 주소의 유형 — `type=clinic` 이면 병/의원, 그 밖은 고르지 않음 */
export const signupTypeFromSearch = (search: string): 'clinic' | null =>
    new URLSearchParams(search).get('type') === 'clinic' ? 'clinic' : null;

/** sessionStorage 키 — 이 탭에서만 남는다 */
export const PENDING_POST_KEY: string = 'ai_dentist_pending_recruitment_post';
/** 담아 둔 공고 원문을 쓸 수 있는 시간(가입·승인 안내를 거쳐도 넉넉히) */
export const PENDING_POST_TTL_MS: number = 24 * 60 * 60 * 1000;

/** sessionStorage 의 쓰는 부분만(시험에서 바꿔 끼운다) */
export interface KeyValueStore {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
}

const browserStore = (): KeyValueStore | null => {
    try {
        return typeof window !== 'undefined' ? window.sessionStorage : null;
    } catch {
        return null;
    }
};

/** [치과 계정으로 가입하고 이대로 올리기] — 공고 원문을 담아 둔다. 담지 못해도(사생활 보호 모드 등) 가입은 막지 않는다 */
export const savePendingPost = (text: string, now: number = Date.now(), store: KeyValueStore | null = browserStore()): boolean => {
    const trimmed: string = text.trim();
    if (!store || !trimmed) return false;
    try {
        store.setItem(PENDING_POST_KEY, JSON.stringify({ text: trimmed, savedAt: now }));
        return true;
    } catch {
        return false;
    }
};

/**
 * 치과 계정의 새 AI 화면 — 담아 둔 공고 원문을 한 번 꺼낸다(꺼내면 지운다). 없거나 시간이 지났거나 모양이 다르면 null(지운다).
 */
export const takePendingPost = (now: number = Date.now(), store: KeyValueStore | null = browserStore()): string | null => {
    if (!store) return null;
    let raw: string | null;
    try {
        raw = store.getItem(PENDING_POST_KEY);
    } catch {
        return null;
    }
    if (raw === null) return null;
    try {
        store.removeItem(PENDING_POST_KEY);
    } catch {
        // 지우지 못해도 아래에서 한 번만 쓴다
    }
    try {
        const value = JSON.parse(raw) as { text?: unknown; savedAt?: unknown };
        if (typeof value.text !== 'string' || !value.text.trim() || typeof value.savedAt !== 'number') return null;
        if (now - value.savedAt > PENDING_POST_TTL_MS || value.savedAt > now + 60_000) return null;
        return value.text;
    } catch {
        return null;
    }
};

/** 이 답 바로 앞 회원 메시지(붙인 공고 원문) — 없으면 null */
export const pastedPostBefore = (messages: { role: string; content: string }[], answerIndex: number): string | null => {
    for (let index = answerIndex - 1; index >= 0; index -= 1) {
        if (messages[index].role === 'USER') return messages[index].content.trim() || null;
    }
    return null;
};
